import React from 'react';

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { PasswordInput } from '../../../components/ui/password-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import type { OrganizationOption, UserFormData } from './types';

export type UserEditFormProps = {
  formData: UserFormData;
  onFieldChange: (field: keyof UserFormData, value: unknown) => void;
  isSaving: boolean;
  isCreating: boolean;
  organizations: OrganizationOption[];
};

export const UserEditForm: React.FC<UserEditFormProps> = ({
  formData,
  onFieldChange,
  isSaving,
  isCreating,
  organizations,
}) => {
  return (
    <>
      {/* Name row */}
      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="user-first-name">First name</Label>
          <Input
            id="user-first-name"
            name="admin-edit-user-first-name"
            value={formData.first_name}
            onChange={(e) => onFieldChange('first_name', e.target.value)}
            autoComplete="off"
            disabled={isSaving}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-last-name">Last name</Label>
          <Input
            id="user-last-name"
            name="admin-edit-user-last-name"
            value={formData.last_name}
            onChange={(e) => onFieldChange('last_name', e.target.value)}
            autoComplete="off"
            disabled={isSaving}
          />
        </div>
      </div>

      {/* Email + Password row */}
      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="user-email">Email</Label>
          <Input
            id="user-email"
            name="admin-edit-user-email"
            type="email"
            value={formData.email}
            onChange={(e) => onFieldChange('email', e.target.value)}
            autoComplete="off"
            disabled={isSaving}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-phone-number">Phone number</Label>
          <Input
            id="user-phone-number"
            name="admin-edit-user-phone-number"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => onFieldChange('phone_number', e.target.value)}
            placeholder="Optional"
            autoComplete="off"
            disabled={isSaving}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-password">Password</Label>
          <PasswordInput
            id="user-password"
            name="admin-set-user-password"
            value={formData.password}
            onChange={(e) => onFieldChange('password', e.target.value)}
            placeholder={isCreating ? 'Required' : 'Leave blank to keep current'}
            autoComplete="new-password"
            disabled={isSaving}
          />
        </div>
      </div>

      {/* Timezone + Org ID row */}
      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="user-timezone">Timezone</Label>
          <Input
            id="user-timezone"
            name="admin-edit-user-timezone"
            value={formData.timezone}
            onChange={(e) => onFieldChange('timezone', e.target.value)}
            placeholder="e.g. America/New_York"
            autoComplete="off"
            disabled={isSaving}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-organization">Organization</Label>
          <Select
            value={formData.organization_id ?? 'none'}
            onValueChange={(value) =>
              onFieldChange('organization_id', value === 'none' ? null : value)
            }
            disabled={isSaving}
          >
            <SelectTrigger id="user-organization">
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="user-organization-role">Organization role</Label>
          <Select
            value={formData.organization_role}
            onValueChange={(value) => onFieldChange('organization_role', value)}
            disabled={isSaving}
          >
            <SelectTrigger id="user-organization-role">
              <SelectValue placeholder="Select organization role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="admin" disabled={!formData.organization_id}>
                Admin
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Toggle switches */}
      <div className="space-y-3">
        <Label>Flags</Label>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <Switch
              id="user-is-staff"
              checked={formData.is_staff}
              onCheckedChange={(checked) => onFieldChange('is_staff', checked)}
              disabled={isSaving}
            />
            <Label htmlFor="user-is-staff" className="cursor-pointer font-normal">
              Staff / Admin
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="user-is-superuser"
              checked={formData.is_superuser}
              onCheckedChange={(checked) => onFieldChange('is_superuser', checked)}
              disabled={isSaving}
            />
            <Label htmlFor="user-is-superuser" className="cursor-pointer font-normal">
              Superuser
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="user-has-completed-intake"
              checked={formData.has_completed_intake}
              onCheckedChange={(checked) => onFieldChange('has_completed_intake', checked)}
              disabled={isSaving}
            />
            <Label htmlFor="user-has-completed-intake" className="cursor-pointer font-normal">
              Completed intake
            </Label>
          </div>
        </div>
      </div>
    </>
  );
};
